import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getShipments = createAsyncThunk(
  "shipment/getShipments",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.SHIPMENT.SLUG}?${queryString}`,
      );
      const shipment_list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { shipment_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getPendingShipmentSalesOrders = createAsyncThunk(
  "shipment/getPendingShipmentSalesOrders",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setPendingSOActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.SHIPMENT.PENDING_SALES_ORDERS}?${queryString}`,
      );
      const list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getShipmentById = createAsyncThunk(
  "shipment/getShipmentById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.SHIPMENT.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const createShipment = createAsyncThunk(
  "shipment/createShipment",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.SHIPMENT.SLUG, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const updateShipment = createAsyncThunk(
  "shipment/updateShipment",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.SHIPMENT.SLUG}/${payload.id}`,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const cancelShipment = createAsyncThunk(
  "shipment/cancelShipment",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(`${API_URL.SHIPMENT.CANCEL}/${id}`);
      dispatch(getShipmentById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const markAsDispatchShipment = createAsyncThunk(
  "shipment/markAsDispatchShipment",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.SHIPMENT.DISPATCH(payload.shipmentId)}`,
        payload,
      );
      dispatch(getShipmentById(payload.shipmentId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const markAsDeliveredShipment = createAsyncThunk(
  "shipment/markAsDeliveredShipment",
  async (shipmentId, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.SHIPMENT.DELIVER(shipmentId)}`,
      );
      dispatch(getShipmentById(shipmentId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getDeliveryChallansForShipment = createAsyncThunk(
  "shipment/getDeliveryChallansForShipment",
  async ({ customerId, shipmentId } = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SHIPMENT.DELIVERY_CHALLANS_BY_CUSTOMER(customerId, shipmentId),
      );
      // Normalize field names — backend may return standard DC model fields
      const dcList = (data.data || []).map((dc) => ({
        ...dc,
        dcId: dc.dcId || dc.id || dc.deliveryChallanId,
        dcNumber: dc.dcNumber || dc.deliveryChallanNumber,
        dcDate: dc.dcDate || dc.deliveryChallanDate,
        totalQty: dc.totalQty ?? dc.totalQuantity,
        soNumber: dc.soNumber || dc.salesOrderNumber,
      }));
      return dcList;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getShipmentCreationEligibility = createAsyncThunk(
  "shipment/getShipmentCreationEligibility",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SHIPMENT.CREATION_ELIGIBILITY(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

const initialState = {
  shipment_list: [],
  shipment_detail: null,
  current_values: null,
  active_payload: null,
  total_records: 0,
  isLoading: false,
  pending_shipment_so_list: [],
  pending_shipment_so_total_records: 0,
  pending_shipment_so_active_payload: null,
  delivery_challans_for_shipment: [],
  delivery_challans_for_shipment_loading: false,
  shipment_creation_eligibility: null,
  shipment_creation_eligibility_loading: false,
};

const shipmentSlice = createSlice({
  name: "shipment",
  initialState,
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    setPendingSOActivePayload(state, action) {
      state.pending_shipment_so_active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.shipment_list = [];
    },
    resetShipmentCreationEligibility(state) {
      state.shipment_creation_eligibility = null;
    },
    resetDeliveryChallansForShipment(state) {
      state.delivery_challans_for_shipment = [];
    },
    resetShipmentDetail(state) {
      state.shipment_detail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getShipments.fulfilled, (state, action) => {
        state.shipment_list = action.payload.shipment_list;
        state.total_records = action.payload.data?.totalRecords || 0;
      })
      // Pending Shipment Sales Orders
      .addCase(getPendingShipmentSalesOrders.fulfilled, (state, action) => {
        state.pending_shipment_so_list = action.payload.list;
        state.pending_shipment_so_total_records =
          action.payload.data.totalRecords;
      })
      .addCase(getShipmentById.fulfilled, (state, action) => {
        state.shipment_detail = action.payload;
      })
      // Delivery Challans for Shipment
      .addCase(getDeliveryChallansForShipment.pending, (state) => {
        state.delivery_challans_for_shipment_loading = true;
      })
      .addCase(getDeliveryChallansForShipment.fulfilled, (state, action) => {
        state.delivery_challans_for_shipment = action.payload;
        state.delivery_challans_for_shipment_loading = false;
      })
      .addCase(getDeliveryChallansForShipment.rejected, (state) => {
        state.delivery_challans_for_shipment = [];
        state.delivery_challans_for_shipment_loading = false;
      })
      // Shipment Creation Eligibility
      .addCase(getShipmentCreationEligibility.pending, (state) => {
        state.shipment_creation_eligibility_loading = true;
      })
      .addCase(getShipmentCreationEligibility.fulfilled, (state, action) => {
        state.shipment_creation_eligibility = action.payload;
        state.shipment_creation_eligibility_loading = false;
      })
      .addCase(getShipmentCreationEligibility.rejected, (state) => {
        state.shipment_creation_eligibility = null;
        state.shipment_creation_eligibility_loading = false;
      });
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  setPendingSOActivePayload,
  resetCurrentValue,
  resetShipmentCreationEligibility,
  resetDeliveryChallansForShipment,
  resetShipmentDetail,
} = shipmentSlice.actions;
export default shipmentSlice.reducer;
